import type { LizenzHinweis as Hinweis } from '@fameacc/contracts';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ladeLizenzHinweis } from '../api/client';
import { bandFuer, type BandArt } from './band';
import { beiLizenzNurLesen } from './lage';

/**
 * Das Hinweisband der Lizenz in der Schale — Auftrag `LZ-oberflaeche`, Spec
 * `docs/superpowers/specs/2026-09-29-lizenzierung-design.md`, Abschnitte 8 und
 * 10.2. Es sitzt unter der Kopfzeile in `app/Shell.tsx` und ist für jeden
 * Angemeldeten sichtbar, auch beim Kunden.
 *
 * **Ein Fehler beim Laden zeigt KEIN Band und keinen Fehler.** Antwortet die
 * Route nicht — ein alter API-Stand ohne `GET /api/v1/lizenz/hinweis`, ein
 * Netzfehler, eine unlesbare Antwort —, bleibt die Oberfläche benutzbar und
 * still. Ein Hinweis, der sich nicht laden lässt, ist kein Betriebsfall, den
 * der Anwender lösen könnte. Ein späterer Fehlschlag beim Auffrischen lässt den
 * zuletzt gelesenen Stand stehen; er löscht ihn nicht.
 *
 * **Geladen wird beim Einhängen, alle zehn Minuten und nach einem
 * `503 LIZENZ_NUR_LESEN`.** Der Zustand ändert sich höchstens täglich (die
 * Freischaltung erneuert sich einmal am Tag); zehn Minuten sind dafür grosszügig
 * und kosten eine leichte Abfrage. Der dritte Anlass ist der Fehlerweg des
 * Klienten (`lage.ts`): Eine Aktion, die an der Sperre scheitert, zeigt sofort
 * denselben Text wie das Band statt einer allgemeinen Fehlermeldung — auch
 * bevor der frische Hinweis da ist, und auch dann, wenn er sich nicht laden
 * lässt. Kommt der frische Hinweis an, gilt er und löst die Erzwingung ab.
 *
 * **Die Kennzeichnung „Entwicklungsbau"** (`bauart: entwicklung`, Spec 10.4)
 * ist dezent und steht für sich: mit dem Band zusammen, wenn eines erscheint,
 * sonst allein. Ein Entwicklungsbau ist nie ein Auslieferungsstand; wer ihn
 * sieht, soll es nicht erst am Systemstatus merken.
 */

/** Wie oft der Hinweis aufgefrischt wird. */
const AUFFRISCHEN_MS = 10 * 60 * 1000;

const BAND_KLASSE: Readonly<Record<BandArt, string>> = {
  betriebsfehler: 'border-destructive/40 bg-destructive/10 text-destructive',
  nurLesen: 'border-destructive/40 bg-destructive/10 text-destructive',
  warnung: 'border-warn bg-warn-soft text-warn',
};

export function LizenzHinweis(): ReactNode {
  const [hinweis, setHinweis] = useState<Hinweis | null>(null);
  const [erzwungenNurLesen, setErzwungenNurLesen] = useState(false);

  const laden = useCallback(async (): Promise<Hinweis | null> => {
    try {
      return await ladeLizenzHinweis();
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    let verworfen = false;

    const auffrischen = async (): Promise<void> => {
      const frisch = await laden();
      if (verworfen || frisch === null) return;
      setHinweis(frisch);
      setErzwungenNurLesen(false);
    };

    void auffrischen();
    const takt = setInterval(() => {
      void auffrischen();
    }, AUFFRISCHEN_MS);
    const abmelden = beiLizenzNurLesen(() => {
      setErzwungenNurLesen(true);
      void auffrischen();
    });

    return () => {
      verworfen = true;
      clearInterval(takt);
      abmelden();
    };
  }, [laden]);

  const band = bandFuer(hinweis, erzwungenNurLesen);
  const entwicklungsbau = hinweis?.bauart === 'entwicklung';
  if (band === null && !entwicklungsbau) return null;

  return (
    <div data-testid="lizenz-hinweis" className="flex flex-col gap-1.5 px-4 pt-3 lg:px-7">
      {band === null ? null : (
        <div
          role={band.art === 'betriebsfehler' ? 'alert' : 'status'}
          aria-label="Lizenzhinweis"
          data-testid="lizenz-band"
          data-art={band.art}
          className={`rounded-md border px-3 py-2 text-sm font-medium ${BAND_KLASSE[band.art]}`}
        >
          {band.text}
        </div>
      )}
      {entwicklungsbau ? (
        <p
          data-testid="lizenz-entwicklungsbau"
          className="text-xs font-medium tracking-wide text-muted-foreground"
        >
          Entwicklungsbau
        </p>
      ) : null}
    </div>
  );
}
